import { ReactNode, useState } from "react";
import { Box } from "@mui/material";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  CollisionDetection,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

// Figma 8619:53992: the dragged chip stays in place as a greyed-out ghost, a full-color copy follows
// the cursor, and a 2px teal line marks the drop target. Chips never shift while dragging.
const DROP_LINE_COLOR = "#039ec3";

export interface ChipRenderOptions {
  /** Props to spread on the drag handle (listeners + a11y attributes). Undefined for the overlay copy. */
  handleProps?: Record<string, unknown>;
  /** Render the greyed-out placeholder left behind while the chip is being dragged. */
  ghost?: boolean;
}

interface ReorderableChipsProps {
  values: string[];
  onChange: (next: string[]) => void;
  renderChip: (value: string, options: ChipRenderOptions) => ReactNode;
}

// Prefer the chip under the pointer; fall back to the nearest one so dropping in a gap still lands.
const collisionDetection: CollisionDetection = (args) => {
  const within = pointerWithin(args);
  return within.length > 0 ? within : closestCenter(args);
};

function DraggableChip({
  value,
  dropSide,
  renderChip,
}: {
  value: string;
  dropSide: "before" | "after" | null;
  renderChip: ReorderableChipsProps["renderChip"];
}) {
  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({ id: value });
  const { setNodeRef: setDropRef } = useDroppable({ id: value });

  return (
    <Box
      ref={(node: HTMLElement | null) => {
        setDragRef(node);
        setDropRef(node);
      }}
      sx={{ position: "relative", display: "inline-flex" }}
    >
      {dropSide && (
        <Box
          data-testid="chip-drop-line"
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            // Centered in the 8px gap between chips.
            [dropSide === "before" ? "left" : "right"]: -5,
            width: 2,
            borderRadius: "2px",
            backgroundColor: DROP_LINE_COLOR,
            pointerEvents: "none",
          }}
        />
      )}
      {renderChip(value, { handleProps: { ...attributes, ...listeners }, ghost: isDragging })}
    </Box>
  );
}

export default function ReorderableChips({ values, onChange, renderChip }: ReorderableChipsProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  // A small distance threshold keeps a plain click on a chip's delete icon from starting a drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor)
  );

  const activeIndex = activeId === null ? -1 : values.indexOf(activeId);
  const overIndex = overId === null ? -1 : values.indexOf(overId);
  // Moving a chip later drops it after the target; moving it earlier drops it before.
  const dropSide: "before" | "after" | null =
    activeIndex === -1 || overIndex === -1 || activeIndex === overIndex
      ? null
      : activeIndex < overIndex
      ? "after"
      : "before";

  const reset = () => {
    setActiveId(null);
    setOverId(null);
  };

  const handleDragStart = ({ active }: DragStartEvent) => setActiveId(String(active.id));
  const handleDragOver = ({ over }: DragOverEvent) => setOverId(over ? String(over.id) : null);
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    const from = values.indexOf(String(active.id));
    const to = over ? values.indexOf(String(over.id)) : -1;
    reset();
    if (from === -1 || to === -1 || from === to) {
      return;
    }
    const next = [...values];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={reset}
    >
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
        {values.map((value) => (
          <DraggableChip
            key={value}
            value={value}
            dropSide={value === overId ? dropSide : null}
            renderChip={renderChip}
          />
        ))}
      </Box>
      <DragOverlay dropAnimation={null}>
        {activeId !== null ? <Box sx={{ cursor: "grabbing" }}>{renderChip(activeId, {})}</Box> : null}
      </DragOverlay>
    </DndContext>
  );
}
